import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { notification } from "top-component-library";
import { generalErrorAlert } from "util/Utils";

import { TECH_SUPPORT_CONTACT_BY_REFERRER } from "./constants";
import { WeeklySchedule } from "./scheduleUtils";
import { SelectedCustomer, StationSchedule, TechSupportContact } from "./types";

interface OwnerPropertiesSubset {
  force_allow_order_ahead?: boolean;
  referrer?: string;
}

export const useSchedules = (selectedCustomer: SelectedCustomer) => {
  const customerId = selectedCustomer?.customer_id;

  const [stations, setStations] = useState<StationSchedule[]>([]);
  const [forceAllowOrderAhead, setForceAllowOrderAhead] = useState(false);
  const [techSupportContact, setTechSupportContact] = useState<TechSupportContact | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const [scheduleRes, propsRes] = await Promise.all([
        axios.post<StationSchedule[]>("/api/getOwnerSchedule/"),
        axios.get<{ owner_properties: OwnerPropertiesSubset }>("api/getOwnerProperties", {
          params: { customer_id: customerId },
        }),
      ]);
      setStations(scheduleRes.data);
      const ownerProperties = propsRes.data.owner_properties;
      setForceAllowOrderAhead(Boolean(ownerProperties?.force_allow_order_ahead));
      setTechSupportContact(TECH_SUPPORT_CONTACT_BY_REFERRER[ownerProperties?.referrer ?? ""] ?? null);
    } catch (error) {
      generalErrorAlert(error, "There was an error loading your schedules, please refresh.");
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  // Mirrors the legacy updateStationSchedule: POST the normalized weekly schedule
  // for a single station. The caller validates/normalizes with parseWeeklySchedule.
  const saveStationSchedule = useCallback(async (stationId: string, schedule: WeeklySchedule) => {
    try {
      await axios.post("/api/ownerUpdateSchedule/", { schedule, station_id: stationId });
      notification.success({ message: "Station settings updated." });
    } catch (error) {
      generalErrorAlert(error, "There was an error updating your Prep Station settings. Please check your inputs.");
    }
  }, []);

  return { stations, forceAllowOrderAhead, techSupportContact, isLoading, saveStationSchedule, refresh: load };
};
